import React, { useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LangSwitcher from "../LangSwitcher";
import { popToPrevSafeRoute } from "../../hooks/useLastSafeRoute";
import { getPdxIfuHref } from "../../utils/navigation/ifuLink";

function NavBar({
  className = "",
  menuOpen = false,
  onToggleMenu,
  onCloseMenu,
  guidePath,
}) {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();

  // Bottom-nav pages (logo shown on these; otherwise show back button)
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();
  const ifuHref = getPdxIfuHref(lang);

  const path = location?.pathname || "";

  // Logo should show on bottom-nav pages EXCEPT home
  const showLogo = path === "/notice" || path === "/about";

  // Home should show neither logo nor back button
  const hideLeftControl = path === "/home";

  // back is only available if we actually have a safe previous route
  const hasSafeBack = useMemo(() => {
    return !hideLeftControl && !showLogo;
  }, [hideLeftControl, showLogo]);

  const showBackButton = !showLogo && !hideLeftControl && hasSafeBack;

  const handleNavigate = useCallback(() => {
    onCloseMenu?.();
  }, [onCloseMenu]);

  // inside NavBar.js
  const handleBack = useCallback(() => {
    onCloseMenu?.();

    const current = `${location?.pathname || ""}${location?.search || ""}`;

    const dest = popToPrevSafeRoute("/home", current);

    navigate(dest); // do NOT replace while debugging
  }, [navigate, onCloseMenu, location?.pathname, location?.search]);

  return (
    <>
      <header
        className={` bg-background flex flex-row sm:grid sm:grid-cols-3 items-center w-full gap-4 z-50 mb-2 ${!menuOpen && "md:border-b md:border-greyscale-1"} ${className}`}
      >
        {/* Left: logo OR back button */}
        <div className="flex items-center gap-2 min-w-0 w-fit justify-start py-2 pl-2 md:pl-4 ">
          {showBackButton ? (
            <button
              type="button"
              onClick={handleBack}
              className="group flex !flex-row gap-1"
              aria-label={t("navBar.back", "Back")}
            >
              <span
                className="material-symbols-outlined text-typogrpahy text-sm group-hover:text-primary group-focus-visible:text-primary"
                aria-hidden="true"
              >
                arrow_back_ios
              </span>
            </button>
          ) : showLogo ? (
            <Link
              className="flex-shrink-0 w-fit"
              to="/home"
              aria-label="Home"
              onClick={handleNavigate}
            >
              <img
                src="/pdx_logo.png"
                alt="pDx"
                className="h-12 w-auto max-w-none title-text"
                loading="lazy"
              />
            </Link>
          ) : (
            <div className="h-12 w-[120px]" aria-hidden="true" />
          )}
        </div>

        {/* CENTER (desktop nav perfectly centered in viewport) */}
        <div className="flex justify-center w-full ">
          <nav className="hidden md:flex items-center gap-4 self-center w-full max-w-lg justify-between ">
            {/* Home */}
            <Link to="/home" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.home", "Home")}
            </Link>

            {/* IFU */}
            <a
              href={ifuHref}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-title"
              onClick={onCloseMenu}
            >
              {t("navBar.ifu_short", "Instructions")}
            </a>

            {/* Results */}
            <Link to="/results" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.results", "Results")}
            </Link>

            {/* Menu toggle */}
            <button
              type="button"
              className={["nav-title", menuOpen ? "text-primary" : ""].join(
                " ",
              )}
              onClick={onToggleMenu}
              aria-expanded={menuOpen}
              aria-label={t("navBar.menu", "Menu")}
            >
              {t("navBar.menu", "Menu")}
            </button>
          </nav>{" "}
          {/* <div className="w-full flex flex-col items-center justify-center">
            {" "}
            <Link
              className="flex-shrink-0 w-fit"
              to="/home"
              aria-label="Home"
              onClick={handleNavigate}
            >
              <img
                src="/purpledx_primary_logo.png"
                alt="pDx"
                className="h-12 w-auto max-w-none title-text"
                loading="lazy"
              />
            </Link>
          </div> */}
        </div>

        {/* Contextual Guide link (only visible on /recording/<variant>/camera) */}
        <div className="self-center hidden md:flex items-center justify-end gap-2 p-1 pr-2 ">
          {guidePath && (
            <Link
              to={guidePath}
              className="small-outlined-button group flex !flex-row gap-1 !w-fit"
            >
              <span
                className="material-symbols-outlined text-primary text-xs
                  group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
              >
                info
              </span>{" "}
              {t("navBar.guide", "Guide")}
            </Link>
          )}
          <LangSwitcher />
        </div>

        {/* Mobile right: Guide CTA + hamburger */}
        <div className="md:hidden flex flex-row gap-2 w-fit items-center justify-end p-1 pr-2 ">
          {guidePath && (
            <Link
              to={guidePath}
              className="small-outlined-button md:hidden group flex !flex-row gap-1 !w-fit whitespace-nowrap"
            >
              <span
                className="material-symbols-outlined text-primary text-xs
                  group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
              >
                info
              </span>
              {t("navBar.guide", "Guide")}
            </Link>
          )}
          <LangSwitcher />
        </div>
      </header>
    </>
  );
}

export default React.memo(NavBar);

// // src/components/NavBar.js
// /* ------------------------------------------------------------------ */
// /* NavBar – responsive top-level navigation                            */
// /* ------------------------------------------------------------------ */
// /**
//  * Behaviour
//  * ----------
//  * • **Mobile (< md)** – hamburger toggles a slide-down, full-height overlay
//  *   containing links: Home · Assessment · Questionnaires · Results · Sign out.
//  * • **Desktop (≥ md)** – keeps inline links and icons.
//  *
//  * @param {object}  props
//  * @param {string=} props.className  Extra Tailwind classes for the `<header>`.
//  * @returns {JSX.Element}
//  */

// import React, { useState, useCallback, useMemo } from "react";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { getAuth, signOut } from "firebase/auth";
// import { firebaseApp } from "../../firebase/firebaseApp";
// import { HomeIcon } from "../icons/HomeIcon";
// import { LogoutIcon } from "../icons/LogoutIcon";
// import { CloseIcon } from "../icons/CloseIcon";
// import { HamburgerIcon } from "../icons/HamburgerIcon";
// import { useTranslation } from "react-i18next";
// import LangSwitcher from "../LangSwitcher";
// import { useAuth } from "../../context/AuthContext";
// import { useProfilePrefetch } from "../../context/ProfilePrefetchContext";
// import { getGuidePathFromCameraPath } from "../../utils/navigation/recordingGuideNav";

// /* ------------------------------------------------------------------ */
// /* Component                                                          */
// /* ------------------------------------------------------------------ */
// function NavBar({ className = "" }) {
//   const { t } = useTranslation();

//   const [menuOpen, setMenuOpen] = useState(false);
//   const navigate = useNavigate();
//   const location = useLocation();

//   const { user } = useAuth();
//   const { clearAllProfiles, clearProfile } = useProfilePrefetch();

//   /**
//    * Guide CTA is shown only on recording camera routes:
//    *   /recording/<variant>/camera
//    * and links to the corresponding:
//    *   /recording/<variant>/guide
//    */
//   const guidePath = useMemo(() => {
//     return getGuidePathFromCameraPath(location?.pathname || "");
//   }, [location?.pathname]);

//   const handleSignOut = useCallback(() => {
//     // Clear local caches first (best-effort)
//     try {
//       if (clearAllProfiles) clearAllProfiles();
//       else if (user?.uid) clearProfile(user.uid);
//     } catch {
//       // ignore
//     }

//     signOut(getAuth(firebaseApp))
//       .then(() => navigate("/auth/login", { replace: true }))
//       .catch(
//         (err) => process.env.NODE_ENV === "development" && console.error(err),
//       );
//   }, [navigate, clearAllProfiles, clearProfile, user?.uid]);

//   /** Close the slide-down menu after a navigation click */
//   const handleNavigate = useCallback(() => setMenuOpen(false), []);

//   return (
//     <>
//       {/* ── Top bar ─────────────────────────────────────────────────── */}
//       <header
//         className={`flex items-center justify-between w-full p-2 gap-4 z-50 ${className}`}
//       >
//         {/* Logo */}
//         <Link
//           className="flex-shrink-0 w-fit"
//           to="/home"
//           aria-label="PDX Home"
//           onClick={handleNavigate}
//         >
//           <img
//             src="/pdx_logo.png"
//             alt="PDX logo"
//             className="h-12 w-auto max-w-none"
//             loading="lazy"
//           />
//         </Link>

//         {/* Desktop icons and links */}
//         <nav className="hidden md:flex items-center gap-4">
//           {/* Desktop links */}
//           <Link to="/questionnaires" className="nav-title min-h-[44px]">
//             {t("navBar.questionnaires")}
//           </Link>
//           <Link to="/about" className="nav-title">
//             {t("navBar.about")}
//           </Link>
//           <Link to="/results" className="nav-title">
//             {t("navBar.results")}
//           </Link>
//           <Link to="/profile" className="nav-title min-h-[44px]">
//             {t("navBar.profile", "Profile")}
//           </Link>
//           {/* Contextual Guide link (only visible on /recording/<variant>/camera) */}
//           {guidePath && (
//             <Link
//               to={guidePath}
//               className="small-outlined-button group flex !flex-row gap-1"
//             >
//               <span
//                 className="material-symbols-outlined text-primary text-xs
//                   group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
//               >
//                 info
//               </span>{" "}
//               {t("navBar.guide", "Guide")}
//             </Link>
//           )}
//           {/* Desktop icons  */}
//           <LangSwitcher />

//           <Link to="/home" aria-label="Home">
//             <HomeIcon />
//           </Link>
//           <button
//             type="button"
//             onClick={handleSignOut}
//             aria-label="Sign out"
//             className="focus:outline-none"
//           >
//             <LogoutIcon />
//           </button>
//         </nav>

//         {/* Contextual Guide link (only visible on /recording/<variant>/camera) */}
//         <div className="md:hidden flex flex-row gap-4 w-full items-center justify-end">
//           {guidePath && (
//             <Link
//               to={guidePath}
//               className="small-outlined-button md:hidden group flex !flex-row gap-1"
//             >
//               <span
//                 className="material-symbols-outlined text-primary text-xs
//                   group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
//               >
//                 info
//               </span>
//               {t("navBar.guide", "Guide")}
//             </Link>
//           )}
//         </div>
//         {/* Hamburger (mobile) */}
//         <button
//           className="md:hidden focus:outline-none"
//           onClick={() => setMenuOpen((o) => !o)}
//           aria-label={menuOpen ? "Close menu" : "Open menu"}
//         >
//           {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
//         </button>
//       </header>

//       {/* ── Slide-down overlay (mobile) ─────────────────────────────── */}
//       {menuOpen && (
//         <div
//           className="fixed inset-0 z-40 bg-white flex flex-col items-start
//                      py-24 p-8 gap-4 animate-[slideDown_0.25s_ease-out] md:hidden"
//         >
//           <LangSwitcher showLabelWhenClosed={true} />

//           <Link
//             to="/home"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.home")}
//           </Link>
//           <Link
//             to="/recording"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.assessment")}
//           </Link>
//           <Link
//             to="/questionnaires"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.questionnaires")}
//           </Link>
//           <Link
//             to="/about"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.about")}
//           </Link>
//           <Link
//             to="/results"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.results")}
//           </Link>
//           <Link
//             to="/profile"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.profile", "Profile")}
//           </Link>
//           <button
//             type="button"
//             onClick={() => {
//               handleSignOut();
//               setMenuOpen(false);
//             }}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.signOut")}
//           </button>
//         </div>
//       )}

//       {/* ── Slide-down keyframe ────────────────────────────────────── */}
//       <style>{`
//         @keyframes slideDown {
//           from { transform: translateY(-100%); }
//           to   { transform: translateY(0); }
//         }
//       `}</style>
//     </>
//   );
// }

// export default React.memo(NavBar);
